/* Independent desktop disclosure; Alchemy retains ownership of the mobile drawer. */
if (!customElements.get('sh-desktop-menu')) {
  customElements.define('sh-desktop-menu', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const on = (el, type, fn) => el.addEventListener(type, fn, { signal: this.abort.signal });
      this.header = this.closest('.header');
      this.toggle = this.querySelector('.sh-menu__toggle');
      this.popup = this.querySelector('.sh-menu__popup');
      // Keep Alchemy navigation if the configured menu has no Shop Now item.
      if (!this.toggle || !this.popup) return;
      this.buttons = [...this.querySelectorAll('.sh-menu__category')];
      this.panels = [...this.querySelectorAll('.sh-menu__panel')];
      this.desktop = matchMedia('(min-width: 1024px)');
      this.inputMode = 'pointer';
      on(this, 'keydown', () => { this.inputMode = 'keyboard'; });
      on(this, 'pointerdown', () => { this.inputMode = 'pointer'; });
      this.active = this.buttons[0];
      if (this.active) this.select(this.active);
      on(this.toggle, 'click', () => this.popup.hidden ? this.open() : this.close());
      this.querySelectorAll('.sh-menu__toplink').forEach(link => {
        on(link, 'focus', () => this.close());
        on(link, 'pointerenter', () => { if (!this.popup.contains(document.activeElement)) this.close(); });
        on(link, 'click', () => this.close());
      });
      on(this.querySelector('.sh-menu__close'), 'click', () => this.close(true));
      this.buttons.forEach((button) => {
        on(button, 'click', () => this.select(button));
        on(button, 'focus', () => this.select(button));
        on(button, 'pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || this.popup.hidden) return;
          if (this.inputMode === 'keyboard' && this.querySelector('.sh-menu__panels').contains(document.activeElement)) return;
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => {
            // Move pointer-acquired focus out of a panel before hiding it.
            if (this.querySelector('.sh-menu__panels').contains(document.activeElement)) button.focus({ preventScroll: true });
            this.select(button);
          }, 90);
        });
        on(button, 'pointerleave', () => clearTimeout(this.hoverTimer));
        on(button, 'keydown', (event) => {
          const controls = [...this.querySelector('.sh-menu__categories').querySelectorAll('a,button')];
          const index = controls.indexOf(button);
          let target;
          if (event.key === 'ArrowDown') target = controls[(index + 1) % controls.length];
          if (event.key === 'ArrowUp') target = controls[(index - 1 + controls.length) % controls.length];
          if (event.key === 'Home') target = controls[0];
          if (event.key === 'End') target = controls[controls.length - 1];
          if (event.key === 'ArrowRight') {
            target = this.panelFor(button)?.querySelector('a');
          }
          if (target) { event.preventDefault(); target.focus(); }
        });
      });
      this.panels.forEach((panel) => on(panel, 'keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault(); this.active.focus();
        }
      }));
      on(this, 'keydown', (event) => {
        if (event.key === 'Escape' && !this.popup.hidden) { event.preventDefault(); event.stopPropagation(); this.close(true); }
        if (event.target === this.toggle && event.key === 'ArrowDown') {
          event.preventDefault(); this.open(); (this.active || this.popup.querySelector('a,button'))?.focus();
        }
      });
      on(this, 'focusout', (event) => {
        // relatedTarget avoids closing between blur and focus during an internal click.
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
      on(document, 'pointerdown', (event) => { if (!this.contains(event.target)) this.close(); });
      on(document, 'on:cart-drawer:before-open', () => this.close());
      on(document, 'on:search-drawer:before-open', () => this.close());
      on(window, 'resize', () => { this.sync(); if (!this.popup.hidden) this.position(); });
      on(window, 'scroll', () => { if (!this.popup.hidden) this.position(); });
      on(this.desktop, 'change', () => this.sync());
      this.resizeObserver = new ResizeObserver(() => { if (!this.popup.hidden) this.position(); });
      this.resizeObserver.observe(this.header);
      this.classList.add('sh-menu--ready');
      this.sync();
    }
    panelFor(button) { return this.panels.find(panel => panel.id === button.getAttribute('aria-controls')); }
    select(button) {
      clearTimeout(this.hoverTimer);
      const changed = this.active !== button;
      this.active = button;
      this.buttons.forEach(item => item.setAttribute('aria-expanded', String(item === button && !this.popup.hidden)));
      this.panels.forEach(panel => { panel.hidden = panel !== this.panelFor(button); });
      if (changed) this.querySelector('.sh-menu__panels').scrollTop = 0;
    }
    position() {
      const bottom = Math.max(0, this.header.getBoundingClientRect().bottom);
      this.style.setProperty('--sh-menu-top', `${bottom}px`);
      this.style.setProperty('--sh-menu-height', `${Math.max(0, innerHeight - bottom - 16)}px`);
    }
    open() {
      if (!this.desktop.matches) return;
      this.position(); this.popup.hidden = false; this.toggle.setAttribute('aria-expanded', 'true');
      if (this.active) this.select(this.active);
    }
    close(returnFocus = false) {
      clearTimeout(this.hoverTimer);
      this.popup.hidden = true; this.toggle.setAttribute('aria-expanded', 'false');
      this.buttons.forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (returnFocus) this.toggle.focus();
    }
    sync() {
      this.header.classList.toggle('sh-menu-enabled', this.desktop.matches);
      if (this.desktop.matches) {
        this.header.classList.remove('header--inline-visible');
        const drawer = this.header.querySelector('menu-drawer');
        if (drawer?.hasAttribute('open') && typeof drawer.close === 'function') drawer.close();
      } else {
        const hadFocus = this.contains(document.activeElement);
        this.close();
        if (hadFocus) this.header.querySelector('.menu-toggle')?.focus();
      }
    }
    disconnectedCallback() {
      clearTimeout(this.hoverTimer); this.resizeObserver?.disconnect(); this.abort?.abort(); this.header?.classList.remove('sh-menu-enabled');
    }
  });
}
